/**
 * ChecklistSettingsTab.js
 *
 * Unified checklist management - Grid of categories with drill-down detail views.
 * OPTION 3: Configuration mode only - Edit buttons, visibility toggles
 */

import React, { useState, useMemo } from 'react';
import {
  AlertTriangle,
  Anchor,
  Users,
  Compass,
  Settings,
  Calendar,
  Clock,
  PlusCircle,
  FileUp,
  ChevronRight,
  Eye,
  EyeOff,
  Lock,
  Info
} from 'lucide-react';
import { CHECKLIST_TYPE_CONFIG, CHECKLIST_IMPORT_TYPES } from '../../../../config/operations/checklistImportConfig';
import { usePreDepartureConfig } from '../../../../hooks/domain/operations/usePreDepartureConfig';
import { useChecklistCategoryVisibility } from '../../../../hooks/domain/operations/useChecklistCategoryVisibility';
import { usePermissions } from '../../../../hooks/usePermissions';
import { NAVIGATION_CHECKLIST_LIST } from '../../../../config/operations/navigationChecklistsLibrary';
import ChecklistCategoryDetail from './ChecklistCategoryDetail';
import ChecklistImportWizard from '../../modules/operations/import/ChecklistImportWizard';
import ChecklistBuilderModal from '../checklists/ChecklistBuilderModal';

// Icon mapping (same as SelectTypeStep)
const ICON_MAP = {
  AlertTriangle,
  Anchor,
  Users,
  Compass,
  Settings,
  Calendar,
  Clock,
  PlusCircle
};

// Color mapping for cards
const COLOR_MAP = {
  red: {
    bg: 'bg-red-100',
    text: 'text-red-600',
    border: 'border-red-200',
    hover: 'hover:border-red-300'
  },
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
    border: 'border-blue-200',
    hover: 'hover:border-blue-300'
  },
  green: {
    bg: 'bg-green-100',
    text: 'text-green-600',
    border: 'border-green-200',
    hover: 'hover:border-green-300'
  },
  cyan: {
    bg: 'bg-cyan-100',
    text: 'text-cyan-600',
    border: 'border-cyan-200',
    hover: 'hover:border-cyan-300'
  },
  amber: {
    bg: 'bg-amber-100',
    text: 'text-amber-600',
    border: 'border-amber-200',
    hover: 'hover:border-amber-300'
  },
  slate: {
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    border: 'border-slate-200',
    hover: 'hover:border-slate-300'
  },
  purple: {
    bg: 'bg-purple-100',
    text: 'text-purple-600',
    border: 'border-purple-200',
    hover: 'hover:border-purple-300'
  },
  indigo: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    border: 'border-indigo-200',
    hover: 'hover:border-indigo-300'
  }
};

/**
 * Category Card Component with Visibility Toggle
 */
const CategoryCard = ({ type, itemCount, onClick, isVisible, isProtected, onToggleVisibility, canEdit }) => {
  const Icon = ICON_MAP[type.icon] || Settings;
  const isEmergency = type.color === 'red';

  const handleVisibilityClick = (e) => {
    e.stopPropagation();
    if (!isProtected && canEdit) {
      onToggleVisibility();
    }
  };

  return (
    <div className="relative">
      <button
        onClick={onClick}
        className={`
          w-full flex items-start gap-4 p-4 rounded-xl bg-white shadow-md ring-1 ring-slate-200/60
          text-left transition-all hover:shadow-lg group
          ${!isVisible ? 'opacity-60' : ''}
        `}
      >
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isEmergency ? 'bg-red-50' : 'bg-sky-50'
        }`}>
          <Icon className={`h-6 w-6 ${isEmergency ? 'text-red-600' : 'text-sky-600'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className={`font-medium ${isEmergency ? 'text-red-700' : 'text-slate-800'}`}>{type.label}</p>
            <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </div>
          <p className="text-sm text-slate-500 mt-0.5">{type.description}</p>
          <p className={`text-xs mt-2 ${isEmergency ? 'text-red-600' : 'text-sky-600'} font-medium`}>
            {itemCount}
          </p>
        </div>
      </button>

      {/* Visibility Toggle Button */}
      <button
        onClick={handleVisibilityClick}
        disabled={isProtected || !canEdit}
        className={`
          absolute top-2 right-2 p-1.5 rounded-lg transition-colors
          ${isProtected
            ? 'text-amber-500 cursor-not-allowed'
            : isVisible
              ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              : 'text-slate-300 hover:text-slate-500 hover:bg-slate-100'
          }
        `}
        title={isProtected ? 'Protected - cannot be hidden' : isVisible ? 'Click to hide from Checklists tab' : 'Click to show in Checklists tab'}
      >
        {isProtected ? (
          <Lock className="h-4 w-4" />
        ) : isVisible ? (
          <Eye className="h-4 w-4" />
        ) : (
          <EyeOff className="h-4 w-4" />
        )}
      </button>
    </div>
  );
};

/**
 * Main ChecklistSettingsTab Component
 */
const ChecklistSettingsTab = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importInitialType, setImportInitialType] = useState(null);
  const [showBuilderModal, setShowBuilderModal] = useState(false);

  // Hooks
  const { getEnabledCountByCategory } = usePreDepartureConfig();
  const { can } = usePermissions();
  const {
    isCategoryVisible,
    isCategoryProtected,
    toggleCategoryVisibility,
    getVisibilitySummary
  } = useChecklistCategoryVisibility();

  const preDepartureCounts = getEnabledCountByCategory();
  const visibilitySummary = getVisibilitySummary();

  // RBAC check - if user can access settings, they can edit
  // The Settings tab itself is already gated, so allow edits
  // Note: can(action, resource) - action first, resource second
  const canEdit = useMemo(() => {
    try {
      if (!can || typeof can !== 'function') return true; // Default to true if no RBAC
      // Check various permission formats - can(action, resource)
      const hasPermission = can('update', 'settings') ||
                           can('read', 'settings'); // If can read settings, allow edit
      return hasPermission !== false; // Default to true unless explicitly denied
    } catch {
      return true; // Default to allowing edits in settings
    }
  }, [can]);

  // Calculate item counts for each category
  const getCategoryCount = useMemo(() => {
    return (typeId) => {
      switch (typeId) {
        case CHECKLIST_IMPORT_TYPES.PRE_DEPARTURE:
          return `${preDepartureCounts.total} items configured`;
        case CHECKLIST_IMPORT_TYPES.EMERGENCY:
          return '11 emergency checklists';
        case CHECKLIST_IMPORT_TYPES.NAVIGATION:
          return `${NAVIGATION_CHECKLIST_LIST?.length || 8} navigation checklists`;
        case CHECKLIST_IMPORT_TYPES.OPERATIONAL:
          return '8 operational checklists';
        case CHECKLIST_IMPORT_TYPES.DAILY:
          return '3 categories';  // FT-80: Engine Room, Bridge, Deck from dailyChecksLibrary
        case CHECKLIST_IMPORT_TYPES.PERIODIC:
          return 'Use Maintenance Module';
        case CHECKLIST_IMPORT_TYPES.CUSTOM:
          return 'User-defined checklists';
        default:
          return 'Configure items';
      }
    };
  }, [preDepartureCounts]);

  // Handle opening import wizard
  const handleOpenImport = (typeId = null) => {
    setImportInitialType(typeId);
    setShowImportWizard(true);
  };

  // Handle import completion
  const handleImportComplete = () => {
    setShowImportWizard(false);
    setImportInitialType(null);
  };

  // If category selected, show detail view
  if (selectedCategory) {
    return (
      <ChecklistCategoryDetail
        categoryId={selectedCategory}
        categoryConfig={CHECKLIST_TYPE_CONFIG[selectedCategory]}
        onBack={() => setSelectedCategory(null)}
        onImport={() => handleOpenImport(selectedCategory)}
      />
    );
  }

  // Get categories for display - FILTER OUT hideFromSettingsTab
  const allTypes = Object.values(CHECKLIST_TYPE_CONFIG);
  const standardTypes = allTypes.filter(t => !t.isCustomType && !t.hideFromSettingsTab);
  const customTypes = allTypes.filter(t => t.isCustomType);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900">Checklist Management</h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure your vessel's operational checklists. Click a category to view and edit items.
        </p>
      </div>

      {/* Visibility Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Category Visibility</p>
          <p className="mt-1">
            Use the <Eye className="h-3.5 w-3.5 inline mx-0.5" /> icon to show/hide categories in the main Checklists tab.
            Categories with <Lock className="h-3.5 w-3.5 inline mx-0.5" /> are safety-critical and always visible.
          </p>
          {visibilitySummary.hidden > 0 && (
            <p className="mt-1 text-blue-600">
              {visibilitySummary.hidden} of {visibilitySummary.total} categories currently hidden.
            </p>
          )}
        </div>
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {standardTypes.map((type) => (
          <CategoryCard
            key={type.id}
            type={type}
            itemCount={getCategoryCount(type.id)}
            onClick={() => setSelectedCategory(type.id)}
            isVisible={isCategoryVisible(type.id)}
            isProtected={isCategoryProtected(type.id)}
            onToggleVisibility={() => toggleCategoryVisibility(type.id)}
            canEdit={canEdit}
          />
        ))}
      </div>

      {/* Custom Checklists Section */}
      {customTypes.length > 0 && (
        <div className="pt-4 border-t border-slate-200">
          <h4 className="text-sm font-medium text-slate-700 mb-3">Custom Checklists</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {customTypes.map((type) => (
              <CategoryCard
                key={type.id}
                type={type}
                itemCount={getCategoryCount(type.id)}
                onClick={() => setSelectedCategory(type.id)}
                isVisible={true}
                isProtected={false}
                onToggleVisibility={() => {}}
                canEdit={canEdit}
              />
            ))}
          </div>
        </div>
      )}

      {/* Global Import Button */}
      <div className="pt-4 border-t border-slate-200 space-y-3">
        {/* Manual Creation Button */}
        <button
          onClick={() => setShowBuilderModal(true)}
          className="w-full flex items-center justify-center gap-3 px-6 py-4
            border-2 border-solid border-cyan-500 rounded-xl
            text-cyan-600 hover:bg-cyan-50 hover:border-cyan-600 transition-colors"
        >
          <PlusCircle className="h-5 w-5" />
          <span className="font-medium">Create New Checklist</span>
        </button>

        {/* AI Import Button */}
        <button
          onClick={() => handleOpenImport(null)}
          className="w-full flex items-center justify-center gap-3 px-6 py-4
            border-2 border-dashed border-cyan-300 rounded-xl
            text-cyan-600 hover:bg-cyan-50 hover:border-cyan-400 transition-colors"
        >
          <FileUp className="h-5 w-5" />
          <span className="font-medium">Import Checklist from SMS Document</span>
          <span className="text-xs bg-cyan-100 px-2 py-0.5 rounded-full">PRO</span>
        </button>
        <p className="text-xs text-slate-500 text-center mt-2">
          Use AI to extract checklist items from your SMS documents
        </p>
      </div>

      {/* Custom Checklist Builder Modal */}
      {showBuilderModal && (
        <ChecklistBuilderModal
          isOpen={showBuilderModal}
          onClose={() => setShowBuilderModal(false)}
          onComplete={(newTemplate) => {
            setShowBuilderModal(false);
          }}
        />
      )}

      {/* Import Wizard Modal */}
      <ChecklistImportWizard
        isOpen={showImportWizard}
        onClose={() => {
          setShowImportWizard(false);
          setImportInitialType(null);
        }}
        onComplete={handleImportComplete}
        initialType={importInitialType}
      />
    </div>
  );
};

export default ChecklistSettingsTab;
